import React, { useState, useRef, useCallback } from "react";
import {
    Box,
    Typography,
    Button,
    Alert,
    Chip,
    TextField,
    InputAdornment,
    IconButton,
    Avatar,
    useTheme,
    useMediaQuery,
    Card,
    CardContent,
    CardActions,
    Divider,
    CircularProgress,
} from "@mui/material";
import {
    Add,
    Business,
    Search,
    Clear,
    Star,
    StarBorder,
    FileDownload,
    Edit,
    Delete,
    OpenInNew,
} from "@mui/icons-material";
import { GridColDef } from "@mui/x-data-grid";
import { DataTable } from "../../components/common/DataTable";
import { BrandForm } from "../../components/forms/BrandForm";
import { productService } from "../../services/productService";
import { Brand } from "../../types";
import { useSearch } from "../../hooks/useSearch";
import { useMobilePagination } from "../../hooks/useMobilePagination";
import { MobilePaginationControls } from "../../components/common/MobilePaginationControls";
import { usePermissions } from "../../hooks/usePermissions";
import { brandPageUrl } from "../../config/storefront";

export const BrandList: React.FC = () => {
    const { canWrite } = usePermissions();
    const [formOpen, setFormOpen] = useState(false);
    const [selectedBrand, setSelectedBrand] = useState<Brand | null>(null);
    const [formMode, setFormMode] = useState<"create" | "edit">("create");
    const [error, setError] = useState<string>("");
    const [exporting, setExporting] = useState(false);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    // Use the reusable search hook
    const {
        searchTerm,
        results: brands,
        loading,
        handleSearchChange,
        handleSearchKeyDown,
        handleClearSearch,
    } = useSearch<Brand>({
        fetchFunction: useCallback(async (params) => {
            if (params.search) {
                // Use the search endpoint
                return await productService.getBrands({
                    search: params.search,
                    limit: params.limit,
                    active_only: params.active_only,
                });
            } else {
                // Use the regular get endpoint
                return await productService.getBrands({
                    limit: params.limit,
                    active_only: params.active_only,
                });
            }
        }, []),
    });

    const {
        currentItems: pagedBrands,
        page: mobilePage,
        totalPages: mobileTotalPages,
        canGoNext: mobileCanGoNext,
        canGoPrev: mobileCanGoPrev,
        goToNext: mobileGoToNext,
        goToPrev: mobileGoToPrev,
        startIndex: mobileStartIndex,
        endIndex: mobileEndIndex,
        total: mobileTotal,
    } = useMobilePagination(brands, 10);

    const handleAdd = () => {
        setSelectedBrand(null);
        setFormMode("create");
        setFormOpen(true);
    };

    const handleEdit = (id: string) => {
        const brand = brands.find((b) => b.brand_id === id);
        if (brand) {
            setSelectedBrand(brand);
            setFormMode("edit");
            setFormOpen(true);
        }
    };

    const handleDelete = async (id: string) => {
        if (window.confirm("Are you sure you want to delete this brand?")) {
            try {
                await productService.deleteBrand(id);
                // Refresh the list after deletion
                window.location.reload(); // Simple refresh for now
            } catch (err) {
                setError("Failed to delete brand");
            }
        }
    };

    // "View" opens the brand's page on the website.
    const handleView = (id: string) => {
        const brand = brands.find((b) => b.brand_id === id);
        if (!brand?.slug) return;
        if (!brand.is_active) {
            setError("Inactive brands don't have a page on the website.");
            return;
        }
        window.open(brandPageUrl(brand.slug), "_blank", "noopener,noreferrer");
    };

    const handleFormSubmit = async (brand: Brand) => {
        setFormOpen(false);
        // Refresh the list after form submission
        window.location.reload(); // Simple refresh for now
    };

    const handleExport = async () => {
        setExporting(true);
        try {
            const blob = await productService.exportBrands({
                search: searchTerm || undefined,
            });

            // Create download link
            const url = window.URL.createObjectURL(new Blob([blob]));
            const link = document.createElement("a");
            link.href = url;
            const dateStr = new Date().toISOString().split("T")[0];
            link.setAttribute("download", `brands_export_${dateStr}.csv`);
            document.body.appendChild(link);
            link.click();
            if (link.parentNode) {
                link.parentNode.removeChild(link);
            }
            window.URL.revokeObjectURL(url);
        } catch (err: any) {
            console.error("Failed to export brands:", err);
            setError("Failed to export brands");
        } finally {
            setExporting(false);
        }
    };

    const columns: GridColDef[] = [
        {
            field: "image_url",
            headerName: "",
            width: 60,
            sortable: false,
            filterable: false,
            renderCell: (params) =>
                params.value ? (
                    <Box
                        component="img"
                        src={params.value}
                        alt=""
                        sx={{
                            width: 36,
                            height: 36,
                            borderRadius: 1,
                            objectFit: "cover",
                        }}
                    />
                ) : (
                    <Business color="disabled" sx={{ fontSize: 28 }} />
                ),
        },
        {
            field: "name",
            headerName: "Brand Name",
            flex: 1,
            minWidth: 180,
        },
        {
            field: "description",
            headerName: "Description",
            flex: 2,
            minWidth: 200,
        },
        {
            field: "display_order",
            headerName: "Order",
            width: 80,
            type: "number",
        },
        {
            field: "is_featured",
            headerName: "Featured",
            width: 100,
            renderCell: (params) =>
                params.value ? (
                    <Star sx={{ color: "warning.main" }} />
                ) : (
                    <StarBorder color="disabled" />
                ),
        },
        {
            field: "is_active",
            headerName: "Status",
            width: 100,
            renderCell: (params) => (
                <Chip
                    label={params.value ? "Active" : "Inactive"}
                    color={params.value ? "success" : "default"}
                    size="small"
                />
            ),
        },
    ];

    const rows = brands.map((brand) => ({
        id: brand.brand_id,
        ...brand,
    }));

    return (
        <Box>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    mb: 3,
                    gap: 2,
                    flexWrap: "wrap",
                }}
            >
                <Typography
                    variant="h4"
                    component="h1"
                    sx={{ fontSize: { xs: "1.35rem", sm: "2.125rem" } }}
                >
                    Brands ({brands.length})
                </Typography>
                <Box
                    sx={{
                        display: "flex",
                        gap: 1,
                        alignItems: "flex-start",
                        flexWrap: "wrap",
                        width: { xs: "100%", sm: "auto" },
                    }}
                >
                    {/* Search Input */}
                    <TextField
                        inputRef={searchInputRef}
                        placeholder="Search brands..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                        onKeyDown={handleSearchKeyDown}
                        size="small"
                        sx={{
                            minWidth: { xs: 0, sm: 250 },
                            width: { xs: "100%", sm: "auto" },
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search color="action" />
                                </InputAdornment>
                            ),
                            endAdornment: searchTerm && (
                                <InputAdornment position="end">
                                    <IconButton
                                        size="small"
                                        onClick={handleClearSearch}
                                    >
                                        <Clear fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Button
                        variant="outlined"
                        startIcon={<FileDownload />}
                        onClick={handleExport}
                        disabled={exporting}
                        size="small"
                    >
                        {isMobile
                            ? exporting
                                ? "..."
                                : "CSV"
                            : exporting
                              ? "Exporting..."
                              : "Export CSV"}
                    </Button>
                    {canWrite && (
                        <Button
                            variant="contained"
                            startIcon={<Add />}
                            onClick={handleAdd}
                            size="small"
                        >
                            {isMobile ? "Add" : "Add Brand"}
                        </Button>
                    )}
                </Box>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {isMobile ? (
                <Box sx={{ mt: 1 }}>
                    {loading ? (
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                py: 6,
                            }}
                        >
                            <CircularProgress />
                        </Box>
                    ) : brands.length === 0 ? (
                        <Box sx={{ textAlign: "center", py: 6 }}>
                            <Typography color="text.secondary">
                                No brands found.
                            </Typography>
                        </Box>
                    ) : (
                        (pagedBrands as any[]).map((brand) => (
                            <Card
                                key={brand.brand_id}
                                variant="outlined"
                                sx={{ mb: 1.5, borderRadius: 2 }}
                            >
                                <CardContent sx={{ pb: "8px !important" }}>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            gap: 1.5,
                                            alignItems: "flex-start",
                                        }}
                                    >
                                        {brand.image_url ? (
                                            <Box
                                                component="img"
                                                src={brand.image_url}
                                                alt={brand.name}
                                                sx={{
                                                    width: 48,
                                                    height: 48,
                                                    borderRadius: 1,
                                                    objectFit: "cover",
                                                    flexShrink: 0,
                                                }}
                                            />
                                        ) : (
                                            <Avatar
                                                variant="rounded"
                                                sx={{
                                                    width: 48,
                                                    height: 48,
                                                    bgcolor: "grey.100",
                                                    flexShrink: 0,
                                                }}
                                            >
                                                <Business color="disabled" />
                                            </Avatar>
                                        )}

                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    justifyContent:
                                                        "space-between",
                                                    alignItems: "flex-start",
                                                    mb: 0.5,
                                                }}
                                            >
                                                <Typography
                                                    variant="body1"
                                                    fontWeight="bold"
                                                    sx={{
                                                        flex: 1,
                                                        mr: 1,
                                                        overflow: "hidden",
                                                        textOverflow:
                                                            "ellipsis",
                                                        whiteSpace: "nowrap",
                                                    }}
                                                >
                                                    {brand.name}
                                                </Typography>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 0.5,
                                                        flexShrink: 0,
                                                    }}
                                                >
                                                    {brand.is_featured ? (
                                                        <Star
                                                            sx={{
                                                                color: "warning.main",
                                                                fontSize: 18,
                                                            }}
                                                        />
                                                    ) : (
                                                        <StarBorder
                                                            color="disabled"
                                                            sx={{
                                                                fontSize: 18,
                                                            }}
                                                        />
                                                    )}
                                                    <Chip
                                                        label={
                                                            brand.is_active
                                                                ? "Active"
                                                                : "Inactive"
                                                        }
                                                        color={
                                                            brand.is_active
                                                                ? "success"
                                                                : "default"
                                                        }
                                                        size="small"
                                                        sx={{
                                                            height: 20,
                                                            fontSize: "0.65rem",
                                                        }}
                                                    />
                                                </Box>
                                            </Box>

                                            {brand.description && (
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{
                                                        display: "block",
                                                        overflow: "hidden",
                                                        textOverflow:
                                                            "ellipsis",
                                                        whiteSpace: "nowrap",
                                                        mb: 0.25,
                                                    }}
                                                >
                                                    {brand.description}
                                                </Typography>
                                            )}

                                            {brand.display_order !==
                                                undefined && (
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                >
                                                    Order: {brand.display_order}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                </CardContent>

                                <Divider />

                                <CardActions sx={{ px: 1.5, py: 1, gap: 1 }}>
                                    {brand.is_active && brand.slug && (
                                        <IconButton
                                            component="a"
                                            href={brandPageUrl(brand.slug)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            aria-label="View on website"
                                            title="View on website"
                                            size="small"
                                            sx={{
                                                border: 1,
                                                borderColor: "divider",
                                                borderRadius: 1,
                                            }}
                                        >
                                            <OpenInNew fontSize="small" />
                                        </IconButton>
                                    )}
                                    {canWrite && (
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<Edit />}
                                            onClick={() =>
                                                handleEdit(brand.brand_id)
                                            }
                                            sx={{ flex: 1 }}
                                        >
                                            Edit
                                        </Button>
                                    )}
                                    {canWrite && (
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            color="error"
                                            startIcon={<Delete />}
                                            onClick={() =>
                                                handleDelete(brand.brand_id)
                                            }
                                            sx={{ flex: 1 }}
                                        >
                                            Delete
                                        </Button>
                                    )}
                                </CardActions>
                            </Card>
                        ))
                    )}
                    <MobilePaginationControls
                        page={mobilePage}
                        totalPages={mobileTotalPages}
                        canGoNext={mobileCanGoNext}
                        canGoPrev={mobileCanGoPrev}
                        onNext={mobileGoToNext}
                        onPrev={mobileGoToPrev}
                        startIndex={mobileStartIndex}
                        endIndex={mobileEndIndex}
                        total={mobileTotal}
                    />
                </Box>
            ) : (
                <DataTable
                    rows={rows}
                    columns={columns}
                    loading={loading}
                    onEdit={canWrite ? handleEdit : undefined}
                    onDelete={canWrite ? handleDelete : undefined}
                    onView={handleView}
                    pageSize={50}
                />
            )}

            <BrandForm
                open={formOpen}
                onClose={() => setFormOpen(false)}
                onSubmit={handleFormSubmit}
                brand={selectedBrand}
                mode={formMode}
            />
        </Box>
    );
};
